import { useState } from 'react';

import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';

interface ExportArgs {
  clientNames: string[];
  from: string;
  to: string;
  includeSessionDetail: boolean;
}

// Mirrors the dash-meta REST + CSRF pattern in
// apps/reveliolabs-dashboard/src/next/DataBuilder/api/save-dataset-api.ts:
// getAuthDashMetaCsrfToken() is synchronous and the token goes in the
// x-surf-token-dash-meta header with credentials included.
export function useUsageAnalyticsExport() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function exportUsage(args: ExportArgs): Promise<void> {
    setIsLoading(true);
    setError(null);
    try {
      const csrfToken = getAuthDashMetaCsrfToken();
      const res = await fetch(`${DASH_META_ROOT}/api/usage-analytics/export`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-surf-token-dash-meta': csrfToken ?? '',
        },
        body: JSON.stringify(args),
      });
      if (!res.ok) {
        throw new Error(`Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const base =
        args.clientNames.length === 1
          ? (args.clientNames[0] ?? '').replace(/[^a-z0-9]+/gi, '_')
          : 'client_groups';
      a.download = `${base}_usage_${args.from}_${args.to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setIsLoading(false);
    }
  }

  return { exportUsage, isLoading, error };
}
